<template>
    <div class="home">
      <!-- Image Slider -->
      <div class="slider-container">
        <div id="homeCarousel" class="carousel slide" data-bs-ride="carousel">
          <div class="carousel-indicators">
            <button v-for="(slide, index) in sliderImages" :key="`indicator-${index}`"
                    type="button" data-bs-target="#homeCarousel" 
                    :data-bs-slide-to="index" :class="{ active: index === 0 }"
                    :aria-current="index === 0" :aria-label="`Slide ${index + 1}`">
            </button>
          </div>
          
          <div class="carousel-inner">
            <div v-for="(slide, index) in sliderImages" :key="`slide-${index}`" 
                 class="carousel-item" :class="{ active: index === 0 }">
              <div class="slider-image" :style="{ backgroundImage: `url(${slide.image_url})` }">
                <div class="carousel-caption">
                  <h1>{{ slide.title }}</h1>
                  <p>{{ slide.description }}</p>
                  <router-link to="/register" class="btn btn-primary btn-lg">
                    <i class="bi bi-person-plus-fill"></i> Register Now
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          
          <button class="carousel-control-prev" type="button" data-bs-target="#homeCarousel" data-bs-slide="prev">
            <span class="carousel-control-prev-icon" aria-hidden="true"></span>
            <span class="visually-hidden">Previous</span>
          </button>
          <button class="carousel-control-next" type="button" data-bs-target="#homeCarousel" data-bs-slide="next">
            <span class="carousel-control-next-icon" aria-hidden="true"></span>
            <span class="visually-hidden">Next</span>
          </button>
        </div>
      </div>
      
      <!-- Introduction Section -->
      <section class="intro-section">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-md-6">
              <h2>Welcome to TutorMed</h2>
              <p class="lead">
                A global online medical community that simplifies complex health concepts for various health personnel.
              </p>
              <p>
                At TutorMed, we believe in making medical education accessible, understandable, and engaging for all 
                healthcare professionals at various stages of their careers.
              </p>
              <router-link to="/about" class="btn btn-outline-primary">
                <i class="bi bi-info-circle"></i> Learn More
              </router-link>
            </div>
            <div class="col-md-6">
              <img src="../assets/founder.jpg" alt="K.A. Obeng Frimpong" class="img-fluid">
            </div>
          </div>
        </div>
      </section>
      
      <!-- User Categories Section -->
      <section class="categories-section">
        <div class="container">
          <h2 class="text-center mb-5">Who We Serve</h2>
          <div class="row g-4">
            <div class="col-md-4 col-sm-6" v-for="(category, index) in userCategories" :key="`category-${index}`">
              <div class="category-card">
                <div class="category-icon">
                  <i :class="category.icon"></i>
                </div>
                <h3>{{ category.title }}</h3>
                <p>{{ category.description }}</p>
                <router-link :to="category.link" class="btn btn-sm btn-outline-primary">
                  <i class="bi bi-book"></i> View Programs
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
  
      <!-- Featured Courses Section -->
      <section class="courses-section">
        <div class="container">
          <div class="section-header text-center">
            <h2>Featured Programs</h2>
            <p>Explore our most popular courses and programs</p>
          </div>
          
          <div class="row g-4">
            <div class="col-lg-4 col-md-6 mb-4" v-for="course in featuredCourses" :key="`course-${course.id}`">
              <div class="course-card">
                <div class="course-image">
                  <img :src="course.thumbnail" :alt="course.title">
                </div>
                <div class="course-content">
                  <h3>{{ course.title }}</h3>
                  <p>{{ course.description ? course.description.substring(0, 100) + '...' : 'Course description coming soon...' }}</p>
                  <router-link :to="course.link" class="btn btn-primary">
                    <i class="bi bi-arrow-right-circle"></i> Learn More
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  
      <!-- Testimonials Section -->
      <section class="testimonials-section">
        <div class="container">
          <h2 class="text-center mb-5">What Our Students Say</h2>
          
          <div class="row">
            <div class="col-md-8 mx-auto">
              <div id="testimonialCarousel" class="carousel slide" data-bs-eide="carousel">
                <div class="carousel-inner">
                  <div class="carousel-item active">
                    <div class="testimonial-card">
                      <div class="testimonial-image">
                        <img src="@/assets/img/testimonials/deborah.jpg" alt="Dr(Med). Deborah Delali Hanyabui" />
                      </div>
                      <div class="testimonial-content">
                        <div class="testimonial-text">
                          <i class="bi bi-quote quote-icon"></i>
                          <p>"TutorMed has been a game-changer for my medical education. The OSCE preparation resources helped me refine my clinical examination skills and approach patients with confidence. The tutors are incredibly knowledgeable and supportive."</p>
                        </div>
                        <div class="testimonial-author">
                          <h4>Dr(Med). Deborah Delali Hanyabui</h4>
                          <p>House Officer, Tema General Hospital</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="carousel-item">
                    <div class="testimonial-card">
                      <div class="testimonial-image">
                        <img src="@/assets/img/testimonials/matthew.jpg" alt="Dr(Med). Matthew Tamakloe" />
                      </div>
                      <div class="testimonial-content">
                        <div class="testimonial-text">
                          <i class="bi bi-quote quote-icon"></i>
                          <p>"As a newly qualified doctor, TutorMed's clinical management modules were exactly what I needed to navigate my house officer rotations. The practical focus helped bridge the gap between theory and real-world practice."</p>
                        </div>
                        <div class="testimonial-author">
                          <h4>Dr(Med). Matthew Tamakloe</h4>
                          <p>House Officer, Tamale Teaching Hospital</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="carousel-item">
                    <div class="testimonial-card">
                      <div class="testimonial-image">
                        <img src="@/assets/img/testimonials/sandra.jpg" alt="Dr(Med). Sandra Esi Eshun" />
                      </div>
                      <div class="testimonial-content">
                        <div class="testimonial-text">
                          <i class="bi bi-quote quote-icon"></i>
                          <p>"The specialized content for medical students at TutorMed covered all essential topics in a clear, understandable way. It allowed me to study at my own pace while balancing clinical rotations."</p>
                        </div>
                        <div class="testimonial-author">
                          <h4>Dr(Med). Sandra Esi Eshun</h4>
                          <p>House Officer, Sunyani Regional Hospital</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="carousel-item">
                    <div class="testimonial-card">
                      <div class="testimonial-image">
                        <img src="@/assets/img/testimonials/pa-icon.png" alt="Francis Agyei" />
                      </div>
                      <div class="testimonial-content">
                        <div class="testimonial-text">
                          <i class="bi bi-quote quote-icon"></i>
                          <p>"TutorMed's pre-scheduled lectures were perfectly tailored to my needs as a physician assistant. The comprehensive coverage of core subjects has significantly improved my diagnostic abilities and patient care."</p>
                        </div>
                        <div class="testimonial-author">
                          <h4>Francis Agyei</h4>
                          <p>Physician Assistant</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <button class="carousel-control-prev" type="button" data-bs-target="#testimonialCarousel" data-bs-slide="prev">
                  <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Previous</span>
                </button>
                <button class="carousel-control-next" type="button" data-bs-target="#testimonialCarousel" data-bs-slide="next">
                  <span class="carousel-control-next-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Next</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
  
      <!-- Inspirational Quotes Section -->
      <section v-if="activeQuotes.length > 0" class="inspirational-showcase">
        <div class="showcase-background">
          <div class="floating-elements">
            <div class="floating-orb orb-1"></div>
            <div class="floating-orb orb-2"></div>
            <div class="floating-orb orb-3"></div>
            <div class="floating-orb orb-4"></div>
          </div>
          <div class="particle-system">
            <div class="particle" v-for="n in 12" :key="n" :style="{ '--i': n }"></div>
          </div>
        </div>
        
        <div class="container">
          <div class="section-header">
            <div class="header-accent"></div>
            <h2 class="showcase-title">
              <span class="title-word">Voices</span>
              <span class="title-word">of</span>
              <span class="title-word highlight">Inspiration</span>
            </h2>
            <p class="showcase-subtitle">-</p>
            <div class="header-decoration">
              <div class="deco-line"></div>
              <div class="deco-diamond"></div>
              <div class="deco-line"></div>
            </div>
          </div>
          
          <div v-if="activeQuotes.length === 1" class="single-inspiration">
            <div class="inspiration-masterpiece">
              <div class="masterpiece-frame">
                <div class="frame-glow"></div>
                <div class="content-wrapper">
                  <div class="visual-section">
                    <div class="portrait-container" v-if="activeQuotes[0].image_url">
                      <div class="portrait-frame">
                        <div class="portrait-glow"></div>
                        <div class="portrait-image">
                          <img :src="activeQuotes[0].image_url" :alt="activeQuotes[0].author || 'Inspirational Leader'">
                        </div>
                        <div class="portrait-effects">
                          <div class="effect-ring ring-1"></div>
                          <div class="effect-ring ring-2"></div>
                          <div class="effect-ring ring-3"></div>
                        </div>
                      </div>
                    </div>
                    <div class="visual-elements">
                      <div class="element-1"></div>
                      <div class="element-2"></div>
                      <div class="element-3"></div>
                    </div>
                  </div>
                  
                  <div class="wisdom-section">
                    <div class="quote-artistry">
                      <div class="quote-frame">
                        <div class="quote-glow"></div>
                        <div class="quote-marks opening">
                          <span>"</span>
                        </div>
                        <blockquote class="wisdom-text">
                          {{ activeQuotes[0].text }}
                        </blockquote>
                        <div class="quote-marks closing">
                          <span>"</span>
                        </div>
                      </div>
                    </div>
                    
                    <div class="speaker-presentation" v-if="activeQuotes[0].author">
                      <div class="speaker-line"></div>
                      <div class="speaker-details">
                        <h3 class="speaker-name">{{ activeQuotes[0].author }}</h3>
                        <p class="speaker-designation">-</p>
                      </div>
                      <div class="speaker-ornament">
                        <div class="ornament-dot"></div>
                        <div class="ornament-lines">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else id="inspirationCarousel" class="carousel slide inspiration-carousel" data-bs-ride="carousel">
            <!-- <div class="carousel-indicators elite-indicators">
              <button v-for="(quote, index) in activeQuotes" :key="`inspiration-indicator-${index}`"
                      type="button" data-bs-target="#inspirationCarousel" 
                      :data-bs-slide-to="index" :class="{ active: index === 0 }"
                      :aria-current="index === 0" :aria-label="`Inspiration ${index + 1}`">
                <span class="indicator-glow"></span>
              </button>
            </div> -->
            
            <div class="carousel-inner masterpiece-gallery">
              <div v-for="(quote, index) in activeQuotes" :key="`inspiration-${index}`" 
                   class="carousel-item" :class="{ active: index === 0 }">
                <div class="inspiration-masterpiece">
                  <div class="masterpiece-frame">
                    <div class="frame-glow"></div>
                    <div class="content-wrapper">
                      <div class="visual-section">
                        <div class="portrait-container" v-if="quote.image_url">
                          <div class="portrait-frame">
                            <div class="portrait-glow"></div>
                            <div class="portrait-image">
                              <img :src="quote.image_url" :alt="quote.author || 'Inspirational Leader'">
                            </div>
                            <div class="portrait-effects">
                              <div class="effect-ring ring-1"></div>
                              <div class="effect-ring ring-2"></div>
                              <div class="effect-ring ring-3"></div>
                            </div>
                          </div>
                        </div>
                        <div class="visual-elements">
                          <div class="element-1"></div>
                          <div class="element-2"></div>
                          <div class="element-3"></div>
                        </div>
                      </div>
                      
                      <div class="wisdom-section">
                        <div class="quote-artistry">
                          <div class="quote-frame">
                            <div class="quote-glow"></div>
                            <div class="quote-marks opening">
                              <span>"</span>
                            </div>
                            <blockquote class="wisdom-text">
                              {{ quote.text }}
                            </blockquote>
                            <div class="quote-marks closing">
                              <span>"</span>
                            </div>
                          </div>
                        </div>
                        
                        <div class="speaker-presentation" v-if="quote.author">
                          <div class="speaker-line"></div>
                          <div class="speaker-details">
                            <h3 class="speaker-name">{{ quote.author }}</h3>
                          </div>
                          <div class="speaker-ornament">
                            <div class="ornament-dot"></div>
                            <div class="ornament-lines">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <button class="carousel-control-prev elite-control" type="button" data-bs-target="#inspirationCarousel" data-bs-slide="prev">
              <div class="control-design">
                <div class="control-glow"></div>
                <span class="control-icon">‹</span>
              </div>
            </button>
            <button class="carousel-control-next elite-control" type="button" data-bs-target="#inspirationCarousel" data-bs-slide="next">
              <div class="control-design">
                <div class="control-glow"></div>
                <span class="control-icon">›</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      <!-- Reviews Section -->
      <section class="reviews-section">
        <div class="container">
          <!-- Display Reviews -->
          <ReviewsList :featured="true" :limit="6" ref="reviewsList" />
          
          <!-- Review Submission Form -->
          <div class="mt-5">
            <ReviewForm @review-submitted="onReviewSubmitted" />
          </div>
        </div>
      </section>

      <!-- Free Resources Banner -->
      <section class="free-resources-banner">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h2><i class="bi bi-lightning-charge"></i> Try Before You Register</h2>
              <p>Experience our content quality with free sample videos and practice questions.</p>
            </div>
            <div class="col-lg-4 text-center text-lg-end">
              <router-link to="/free-resources" class="btn btn-outline-light btn-lg">
                <i class="bi bi-play-circle"></i> Access Free Resources
              </router-link>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Call to Action Section -->
      <section class="cta-section">
        <div class="container">
          <div class="cta-content text-center">
            <h2>Ready to Enhance Your Medical Knowledge?</h2>
            <p>Join thousands of healthcare professionals who are already benefiting from TutorMed's programs.</p>
            <router-link to="/register" class="btn btn-light btn-lg">
              <i class="bi bi-check2-circle"></i> Register Today
            </router-link>
          </div>
        </div>
      </section>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as bootstrap from 'bootstrap'
import api from '@/services/api'
import ReviewForm from '@/components/ReviewForm.vue'
import ReviewsList from '@/components/ReviewsList.vue'

export default {
  name: 'HomeView',
  components: {
    ReviewForm,
    ReviewsList
  },
  data() {
    return {
      activeQuotes: [],
      quotesRetryAttempted: false,
      userCategories: [
        {
          title: 'Medical Students',
          description: 'Comprehensive courses covering both MCQ and OSCE preparation.',
          icon: 'bi bi-book',
          link: '/programs/medical-students'
        },
        {
          title: 'House Officers / Doctors',
          description: 'Clinical management and diagnostic training for practitioners.',
          icon: 'bi bi-person-badge',
          link: '/programs/doctors'
        },
        {
          title: 'GMDC Candidates',
          description: 'Specialized preparation for GMDC examinations.',
          icon: 'bi bi-file-earmark-text',
          link: '/programs/gmdc'
        },
        {
          title: 'Physician Assistants',
          description: 'Offline courses with access to pre-scheduled lectures.',
          icon: 'bi bi-clipboard2-pulse',
          link: '/programs/physician-assistants'
        },
        {
          title: 'Nurses',
          description: 'Online courses covering essential nursing modules.',
          icon: 'bi bi-heart-pulse',
          link: '/programs/nurses'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['sliderImages', 'featuredCourses', 'testimonials'])
  },
  methods: {
    ...mapActions(['fetchHomepageData']),
    async fetchQuotes() {
      try {
        console.log('📱 Fetching quotes - device info:', {
          userAgent: navigator.userAgent,
          mobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
          screen: `${screen.width}x${screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`
        });
        
        // Try with timeout and retry for mobile devices
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const response = await this.$api.get('/users/quotes/', {
          timeout: isMobile ? 15000 : 10000, // Longer timeout for mobile
          headers: {
            'X-Device-Type': isMobile ? 'mobile' : 'desktop',
            'X-Non-Critical': 'true' // Mark as non-critical to avoid login redirects
          }
        });
        console.log('✅ Quotes API response received:', {
          status: response.status,
          dataLength: response.data?.length || 0,
          activeQuotes: response.data?.filter(quote => quote.is_active)?.length || 0
        });
        
        // Filter only active quotes
        this.activeQuotes = response.data.filter(quote => quote.is_active);
        
        // If no active quotes from API, use fallback
        if (this.activeQuotes.length === 0) {
          console.log('⚠️ No active quotes found, using fallback');
          this.setFallbackQuote();
        } else {
          console.log('✨ Successfully loaded active quotes:', this.activeQuotes.length);
        }
      } catch (error) {
        console.error('❌ Error fetching quotes:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          mobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        });
        
        // Retry once for mobile devices with different approach
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile && !this.quotesRetryAttempted) {
          console.log('🔄 Retrying quotes API for mobile device...');
          this.quotesRetryAttempted = true;
          
          try {
            // Simple retry with axios directly
            const axios = (await import('axios')).default;
            const retryResponse = await axios.get('https://tutormed.org/api/users/quotes/', {
              timeout: 20000,
              withCredentials: false // Try without credentials for mobile
            });
            
            if (retryResponse.data && Array.isArray(retryResponse.data)) {
              this.activeQuotes = retryResponse.data.filter(quote => quote.is_active);
              console.log('✅ Retry successful, loaded quotes:', this.activeQuotes.length);
              return;
            }
          } catch (retryError) {
            console.error('❌ Retry also failed:', retryError.message);
          }
        }
        
        // Use fallback quote if all attempts fail
        console.log('🔄 Using fallback quote due to API error');
        this.setFallbackQuote();
      }
    },
    setFallbackQuote() {
      // Fallback inspirational quote for when API fails or no quotes available
      this.activeQuotes = [{
        id: 'fallback',
        text: "Excellence is not just our standard, it's our promise to every healthcare professional we serve.",
        author: "TutorMed Team",
        title: "Educational Excellence",
        image_url: null,
        is_active: true
      }];
    },
    initCarousel() {
      // Initialize Bootstrap carousel
      const carousel = document.getElementById('homeCarousel');
      if (carousel) {
        new bootstrap.Carousel(carousel, {
          interval: 5000,
          wrap: true,
          keyboard: true
        });
      }
      
      // Initialize inspiration carousel if exists
      const inspirationCarousel = document.getElementById('inspirationCarousel');
      if (inspirationCarousel && this.activeQuotes.length > 1) {
        new bootstrap.Carousel(inspirationCarousel, {
          interval: 10000,
          wrap: true,
          keyboard: true
        });
      }
    },
    onReviewSubmitted() {
      // Refresh the reviews list when a new review is submitted
      if (this.$refs.reviewsList) {
        this.$refs.reviewsList.refresh()
      }
    }
  },
  mounted() {
    // Initialize carousel after component is mounted
    this.$nextTick(() => {
      this.initCarousel();
    });
  },
  async created() {
    // Fetch homepage data which now always uses the hardcoded featured courses and testimonials
    this.fetchHomepageData().catch(error => {
      console.error('Error fetching homepage data:', error);
    });
    
    // Fetch quotes for the flyers section
    await this.fetchQuotes();
    
    // Re-initialize carousel after quotes are loaded
    this.$nextTick(() => {
      this.initCarousel();
    });
  }
}
</script>

<style scoped lang="scss">
.slider-container {
  height: 600px;
  margin-bottom: 60px;
  
  .carousel, .carousel-inner, .carousel-item {
    height: 100%;
  }
  
  .slider-image {
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
    }
  }
  
  @media (max-width: 768px) {
    height: 400px;
  }
  
  .carousel-caption {
    top: 50%;
    transform: translateY(-50%);
    bottom: auto;
    padding: 20px;
    
    h1 {
      font-size: 3.2rem;
      font-weight: 700;
      margin-bottom: 1rem;
      text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    }
    
    p {
      font-size: 1.3rem;
      margin-bottom: 2rem;
      text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
    }
    
    .btn {
      padding: 12px 30px;
      font-weight: 600;
      border-radius: 5px;
      transition: all 0.3s ease;
      background-color: var(--primary-color);
      border-color: var(--primary-color);
      
      i {
        margin-right: 8px;
      }
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
      }
    }
  }
  
  .carousel-indicators {
    bottom: 20px;
    
    button {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.7);
      margin: 0 5px;
      
      &.active {
        background-color: var(--primary-color);
      }
    }
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.intro-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background-color: rgba(0, 206, 201, 0.1);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -150px;
    left: -150px;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background-color: rgba(0, 206, 201, 0.05);
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  h2 {
    color: var(--primary-color);
    margin-bottom: 1.5rem;
    font-weight: 700;
    font-size: 2.5rem;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -10px;
      width: 50px;
      height: 3px;
      background-color: var(--primary-color);
    }
  }
  
  .lead {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
    color: #495057;
  }
  
  p {
    color: #6c757d;
    margin-bottom: 2rem;
    line-height: 1.8;
  }
  
  .btn-outline-primary {
    border-width: 2px;
    font-weight: 600;
    padding: 10px 25px;
    border-radius: 30px;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(0, 206, 201, 0.3);
    }
  }
  
  img {
    border-radius: 10px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    }
  }
}

.categories-section {
  padding: 100px 0;
  background-color: white;
  position: relative;
  
  h2 {
    position: relative;
    display: inline-block;
    margin: 0 auto 60px;
    font-weight: 700;
    color: #333;
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: -15px;
      width: 80px;
      height: 3px;
      background-color: var(--primary-color);
      transform: translateX(-50%);
    }
  }
  
  .category-card {
    background: #fff;
    border-radius: 15px;
    padding: 40px 25px;
    text-align: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    height: 100%;
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    z-index: 1;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(0, 206, 201, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.4s ease;
    }
    
    &:hover {
      transform: translateY(-15px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
      
      &::before {
        opacity: 1;
      }
      
      .category-icon {
        transform: scale(1.1);
        color: var(--primary-color);
        
        i {
          text-shadow: 0 0 10px rgba(0, 206, 201, 0.3);
        }
      }
      
      .btn {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .category-icon {
      font-size: 3.5rem;
      color: #555;
      margin-bottom: 25px;
      transition: all 0.3s ease;
      display: inline-block;
      
      i {
        background: -webkit-linear-gradient(45deg, var(--primary-color), #20e3de);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }
    
    h3 {
      font-size: 1.6rem;
      margin-bottom: 15px;
      color: #333;
      font-weight: 600;
    }
    
    p {
      color: #6c757d;
      margin-bottom: 25px;
      line-height: 1.7;
    }
    
    .btn {
      border-radius: 30px;
      padding: 8px 20px;
      font-weight: 600;
      font-size: 0.9rem;
      opacity: 0.9;
      transform: translateY(5px);
      transition: all 0.3s ease;
    }
  }
}

.courses-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('data:image/svg+xml,<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="2" fill="%2300CEC9" opacity="0.15"/></svg>');
    background-repeat: repeat;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  .section-header {
    margin-bottom: 60px;
    text-align: center;
    
    h2 {
      color: #333;
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 15px;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: -15px;
        width: 80px;
        height: 3px;
        background-color: var(--primary-color);
        transform: translateX(-50%);
      }
    }
    
    p {
      color: #6c757d;
      max-width: 600px;
      margin: 0 auto;
      font-size: 1.1rem;
      margin-top: 25px;
    }
  }
  
  .course-card {
    background: #fff;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
    height: 100%;
    transition: all 0.4s ease;
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    top: 0;
    
    &:hover {
      box-shadow: 0 15px 40px rgba(0,0,0,0.12);
      top: -10px;
      
      .course-image {
        img {
          transform: scale(1.1);
        }
      }
      
      .btn {
        background-color: var(--primary-color);
        color: white;
        transform: translateY(-3px);
        box-shadow: 0 10px 15px rgba(0, 206, 201, 0.2);
      }
    }
    
    .course-image {
      height: 200px;
      overflow: hidden;
      position: relative;
      background-color: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 100%);
        z-index: 1;
      }
      
      img {
        max-width: 100%;
        max-height: 90%;
        object-fit: contain;
        padding: 20px;
        transition: all 0.5s ease;
        position: relative;
        z-index: 2;
      }
    }
    
    .course-content {
      padding: 30px 25px;
      display: flex;
      flex-direction: column;
      height: 230px;
      
      h3 {
        font-size: 1.25rem;
        margin-bottom: 15px;
        color: #333;
        font-weight: 600;
        line-height: 1.4;
        height: 42px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      
      p {
        margin-bottom: 25px;
        color: #6c757d;
        line-height: 1.7;
        flex-grow: 1;
        height: 75px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
      
      .btn {
        border-radius: 30px;
        padding: 10px 25px;
        border: 2px solid var(--primary-color);
        background-color: transparent;
        color: var(--primary-color);
        font-weight: 600;
        transition: all 0.3s ease;
        display: inline-block;
        align-self: flex-start;
      }
    }
  }
}

// Inspirational Showcase - World-Class Design
.inspirational-showcase {
  position: relative;
  padding: 120px 0 140px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 80px 0 100px;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0 80px;
  }
  
  .showcase-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(138, 43, 226, 0.12) 0%, transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(255, 20, 147, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(0, 191, 255, 0.1) 0%, transparent 40%),
      linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2d1b69 50%, #0f0f23 100%);
    
    .floating-elements {
      position: absolute;
      width: 100%;
      height: 100%;
      
      @media (max-width: 768px) {
        display: none; /* Disable heavy animations on mobile for better performance */
      }
      
      .floating-orb {
        position: absolute;
        border-radius: 50%;
        opacity: 0.6;
        animation: orbFloat 15s ease-in-out infinite;
        
        &.orb-1 {
          width: 200px;
          height: 200px;
          top: 10%;
          left: 5%;
          background: radial-gradient(circle, rgba(0, 255, 255, 0.15) 0%, transparent 70%);
          animation-delay: 0s;
        }
        
        &.orb-2 {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 10%;
          background: radial-gradient(circle, rgba(255, 105, 180, 0.12) 0%, transparent 70%);
          animation-delay: -5s;
        }
        
        &.orb-3 {
          width: 300px;
          height: 300px;
          bottom: 20%;
          left: 15%;
          background: radial-gradient(circle, rgba(138, 43, 226, 0.08) 0%, transparent 70%);
          animation-delay: -10s;
        }
        
        &.orb-4 {
          width: 120px;
          height: 120px;
          top: 20%;
          right: 25%;
          background: radial-gradient(circle, rgba(0, 206, 201, 0.2) 0%, transparent 70%);
          animation-delay: -7s;
        }
      }
    }
    
    .particle-system {
      position: absolute;
      width: 100%;
      height: 100%;
      
      @media (max-width: 768px) {
        display: none; /* Disable particle system on mobile for better performance */
      }
      
      .particle {
        position: absolute;
        width: 3px;
        height: 3px;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%);
        border-radius: 50%;
        animation: particleFloat calc(8s + var(--i) * 2s) ease-in-out infinite;
        top: calc(10% + var(--i) * 7%);
        left: calc(5% + var(--i) * 8%);
        animation-delay: calc(var(--i) * -0.5s);
      }
    }
  }
  
  .section-header {
    text-align: center;
    margin-bottom: 100px;
    position: relative;
    z-index: 2;
    
    .header-accent {
      width: 100px;
      height: 4px;
      background: linear-gradient(90deg, transparent 0%, #00CEC9 50%, transparent 100%);
      margin: 0 auto 30px;
      border-radius: 2px;
      opacity: 0.7;
    }
    
    .showcase-title {
      font-size: 4.5rem;
      font-weight: 900;
      margin-bottom: 30px;
      line-height: 1.1;
      
      @media (max-width: 768px) {
        font-size: 3rem;
      }
      
      @media (max-width: 480px) {
        font-size: 2.5rem;
        margin-bottom: 20px;
      }
      
      .title-word {
        display: inline-block;
        margin: 0 15px;
        background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 50%, #cbd5e0 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        text-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
        animation: titleReveal 0.8s ease-out forwards;
        opacity: 0;
        transform: translateY(50px);
        
        &:nth-child(1) { animation-delay: 0.2s; }
        &:nth-child(2) { animation-delay: 0.4s; }
        &:nth-child(3) { animation-delay: 0.6s; }
        
        &.highlight {
          background: linear-gradient(135deg, #00CEC9 0%, #4299e1 50%, #667eea 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }
    }
    
    .showcase-subtitle {
      font-size: 1.4rem;
      color: rgba(255, 255, 255, 0.8);
      font-weight: 300;
      max-width: 700px;
      margin: 0 auto 40px;
      line-height: 1.6;
      opacity: 0;
      animation: fadeIn 1s ease-out 1s forwards;
    }
    
    .header-decoration {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
      opacity: 0;
      animation: fadeIn 1s ease-out 1.2s forwards;
      
      .deco-line {
        width: 60px;
        height: 1px;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
      }
      
      .deco-diamond {
        width: 8px;
        height: 8px;
        background: #00CEC9;
        transform: rotate(45deg);
        box-shadow: 0 0 20px rgba(0, 206, 201, 0.6);
      }
    }
  }
  
  .inspiration-masterpiece {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    
    .masterpiece-frame {
      position: relative;
      background: 
        linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
      border-radius: 32px;
      padding: 60px;
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      box-shadow: 
        0 40px 80px rgba(0, 0, 0, 0.25),
        0 20px 40px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
      transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      
      &:hover {
        transform: translateY(-12px) scale(1.02);
        box-shadow: 
          0 60px 120px rgba(0, 0, 0, 0.3),
          0 30px 60px rgba(0, 0, 0, 0.2),
          inset 0 1px 0 rgba(255, 255, 255, 0.4);
      }
      
      @media (max-width: 992px) {
        padding: 40px 30px;
      }
      
      @media (max-width: 768px) {
        padding: 30px 20px;
        margin: 0 15px;
      }
      
      @media (max-width: 480px) {
        padding: 20px 15px;
        margin: 0 10px;
      }
      
      .frame-glow {
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, 
          rgba(0, 206, 201, 0.3) 0%, 
          rgba(66, 153, 225, 0.2) 25%,
          rgba(138, 43, 226, 0.3) 50%,
          rgba(255, 20, 147, 0.2) 75%,
          rgba(0, 206, 201, 0.3) 100%);
        border-radius: 34px;
        z-index: -1;
        opacity: 0;
        transition: opacity 0.6s ease;
        animation: glowPulse 4s ease-in-out infinite;
      }
      
      &:hover .frame-glow {
        opacity: 1;
      }
      
      .content-wrapper {
        display: grid;
        grid-template-columns: 1fr 1.8fr;
        gap: 80px;
        align-items: center;
        
        @media (max-width: 992px) {
          grid-template-columns: 1fr;
          gap: 50px;
          text-align: center;
        }
      }
    }
  }
  
  .visual-section {
    position: relative;
    
    .portrait-container {
      position: relative;
      width: 320px;
      height: 400px;
      margin: 0 auto;
      
      @media (max-width: 992px) {
        width: 280px;
        height: 350px;
      }
      
      @media (max-width: 768px) {
        width: 250px;
        height: 300px;
      }
      
      @media (max-width: 480px) {
        width: 200px;
        height: 250px;
      }
      
      .portrait-frame {
        position: relative;
        width: 100%;
        height: 100%;
        
        .portrait-glow {
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          background: linear-gradient(135deg, 
            rgba(0, 206, 201, 0.4) 0%, 
            rgba(66, 153, 225, 0.3) 50%,
            rgba(138, 43, 226, 0.4) 100%);
          border-radius: 24px;
          filter: blur(20px);
          opacity: 0.6;
          animation: portraitGlow 3s ease-in-out infinite alternate;
        }
        
        .portrait-image {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.3),
            0 12px 25px rgba(0, 0, 0, 0.2);
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.6s ease;
          }
          
          &:hover img {
            transform: scale(1.05);
          }
        }
        
        .portrait-effects {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          
          .effect-ring {
            position: absolute;
            border-radius: 50%;
            border: 2px solid rgba(255, 255, 255, 0.2);
            
            &.ring-1 {
              width: 380px;
              height: 380px;
              top: -190px;
              left: -190px;
              animation: ringPulse 4s ease-in-out infinite;
            }
            
            &.ring-2 {
              width: 420px;
              height: 420px;
              top: -210px;
              left: -210px;
              animation: ringPulse 4s ease-in-out infinite reverse;
              animation-delay: -1s;
            }
            
            &.ring-3 {
              width: 460px;
              height: 460px;
              top: -230px;
              left: -230px;
              animation: ringPulse 4s ease-in-out infinite;
              animation-delay: -2s;
            }
          }
        }
      }
    }
    
    .visual-elements {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      
      .element-1,
      .element-2,
      .element-3 {
        position: absolute;
        border-radius: 50%;
        background: linear-gradient(135deg, rgba(0, 206, 201, 0.1) 0%, rgba(66, 153, 225, 0.1) 100%);
        animation: elementFloat 6s ease-in-out infinite;
      }
      
      .element-1 {
        width: 60px;
        height: 60px;
        top: 20%;
        right: -30px;
        animation-delay: 0s;
      }
      
      .element-2 {
        width: 40px;
        height: 40px;
        bottom: 30%;
        left: -20px;
        animation-delay: -2s;
      }
      
      .element-3 {
        width: 80px;
        height: 80px;
        top: 60%;
        right: -40px;
        animation-delay: -4s;
      }
    }
  }
  
  .wisdom-section {
    position: relative;
    
    .quote-artistry {
      margin-bottom: 50px;
      
      .quote-frame {
        position: relative;
        padding: 40px 20px;
        
        .quote-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0, 206, 201, 0.05) 0%, rgba(66, 153, 225, 0.05) 100%);
          border-radius: 16px;
          filter: blur(10px);
        }
        
        .quote-marks {
          position: absolute;
          font-size: 8rem;
          font-family: Georgia, serif;
          font-weight: bold;
          line-height: 1;
          opacity: 0.15;
          color: #00CEC9;
          
          &.opening {
            top: -20px;
            left: -20px;
            
            span {
              background: linear-gradient(135deg, #00CEC9 0%, #4299e1 100%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
          }
          
          &.closing {
            bottom: -60px;
            right: -20px;
            transform: rotate(180deg);
            
            span {
              background: linear-gradient(135deg, #4299e1 0%, #667eea 100%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
          }
        }
        
        .wisdom-text {
          position: relative;
          font-size: 2.2rem;
          line-height: 1.6;
          color: #2d3748;
          font-weight: 400;
          font-style: italic;
          margin: 0;
          z-index: 2;
          
          @media (max-width: 768px) {
            font-size: 1.8rem;
          }
          
          @media (max-width: 576px) {
            font-size: 1.5rem;
            line-height: 1.5;
          }
          
          @media (max-width: 480px) {
            font-size: 1.3rem;
            line-height: 1.4;
          }
        }
      }
    }
    
    .speaker-presentation {
      position: relative;
      display: flex;
      align-items: center;
      gap: 30px;
      
      @media (max-width: 992px) {
        justify-content: center;
        flex-direction: column;
        gap: 20px;
      }
      
      .speaker-line {
        width: 80px;
        height: 3px;
        background: linear-gradient(90deg, #00CEC9 0%, #4299e1 100%);
        border-radius: 2px;
        box-shadow: 0 0 10px rgba(0, 206, 201, 0.4);
        
        @media (max-width: 992px) {
          order: 2;
        }
      }
      
      .speaker-details {
        flex: 1;
        
        @media (max-width: 992px) {
          order: 1;
          text-align: center;
        }
        
        .speaker-name {
          font-size: 2.2rem;
          font-weight: 800;
          color: #1a202c;
          margin-bottom: 8px;
          background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          
          @media (max-width: 768px) {
            font-size: 1.8rem;
          }
        }
        
        .speaker-designation {
          font-size: 1.2rem;
          color: #4a5568;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 2px;
          opacity: 0.9;
          margin: 0;
        }
      }
      
      .speaker-ornament {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        
        @media (max-width: 992px) {
          order: 3;
        }
        
        .ornament-dot {
          width: 12px;
          height: 12px;
          background: linear-gradient(135deg, #00CEC9 0%, #4299e1 100%);
          border-radius: 50%;
          box-shadow: 0 0 15px rgba(0, 206, 201, 0.6);
        }
        
        .ornament-lines {
          display: flex;
          flex-direction: column;
          gap: 4px;
          
          span {
            width: 20px;
            height: 1px;
            background: linear-gradient(90deg, transparent 0%, #4a5568 50%, transparent 100%);
            
            &:nth-child(2) {
              width: 30px;
            }
          }
        }
      }
    }
  }
  
  .single-inspiration {
    animation: masterpieceReveal 1.2s ease-out forwards;
  }
  
  .inspiration-carousel {
    position: relative;
    
    .elite-indicators {
      bottom: -80px;
      
      button {
        position: relative;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        margin: 0 12px;
        background: rgba(255, 255, 255, 0.2);
        border: 2px solid rgba(255, 255, 255, 0.3);
        transition: all 0.4s ease;
        overflow: hidden;
        
        .indicator-glow {
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(135deg, #00CEC9 0%, #4299e1 100%);
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        
        &.active {
          background: rgba(0, 206, 201, 0.8);
          border-color: #00CEC9;
          box-shadow: 0 0 25px rgba(0, 206, 201, 0.6);
          
          .indicator-glow {
            opacity: 1;
            animation: indicatorPulse 2s ease-in-out infinite;
          }
        }
      }
    }
    
    .elite-control {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 80px;
      height: 80px;
      background: none;
      border: none;
      z-index: 10;
      
      .control-design {
        position: relative;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.1);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.4s ease;
        backdrop-filter: blur(15px);
        
        .control-glow {
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          background: linear-gradient(135deg, #00CEC9 0%, #4299e1 100%);
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.4s ease;
          filter: blur(8px);
        }
        
        .control-icon {
          font-size: 2rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: bold;
          z-index: 2;
          transition: color 0.4s ease;
        }
        
        &:hover {
          background: rgba(0, 206, 201, 0.2);
          border-color: rgba(0, 206, 201, 0.5);
          box-shadow: 0 0 40px rgba(0, 206, 201, 0.4);
          
          .control-glow {
            opacity: 0.8;
          }
          
          .control-icon {
            color: #ffffff;
          }
        }
      }
      
      &.carousel-control-prev {
        left: -50px;
        
        @media (max-width: 768px) {
          left: 20px;
        }
        
        @media (max-width: 480px) {
          left: 10px;
          width: 45px;
          height: 45px;
        }
      }
      
      &.carousel-control-next {
        right: -50px;
        
        @media (max-width: 768px) {
          right: 20px;
        }
        
        @media (max-width: 480px) {
          right: 10px;
          width: 45px;
          height: 45px;
        }
      }
    }
  }
}

// Advanced Animations
@keyframes orbFloat {
  0%, 100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.6;
  }
  33% {
    transform: translateY(-30px) translateX(20px) rotate(120deg);
    opacity: 0.8;
  }
  66% {
    transform: translateY(20px) translateX(-15px) rotate(240deg);
    opacity: 0.4;
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-50px) translateX(30px) scale(1.2);
    opacity: 0.8;
  }
}

@keyframes titleReveal {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

@keyframes glowPulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.02);
  }
}

@keyframes portraitGlow {
  0% {
    opacity: 0.4;
    filter: blur(20px) brightness(1);
  }
  100% {
    opacity: 0.8;
    filter: blur(25px) brightness(1.2);
  }
}

@keyframes ringPulse {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.05);
  }
}

@keyframes elementFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 1;
  }
}

@keyframes masterpieceReveal {
  0% {
    opacity: 0;
    transform: translateY(60px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes indicatorPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.reviews-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="2" fill="%2300CEC9" opacity="0.1"/></svg>');
    background-repeat: repeat;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
}

.free-resources-banner {
  background-color: #333;
  background-image: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.7) 100%);
  background-size: cover;
  background-position: center;
  color: white;
  padding: 40px 0;
  margin-top: 30px;
  position: relative;
  
  h2 {
    font-weight: 700;
    font-size: 1.8rem;
    margin-bottom: 10px;
    
    i {
      color: var(--primary-color);
      margin-right: 10px;
    }
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 0;
    opacity: 0.9;
  }
  
  .btn {
    border: 2px solid white;
    font-weight: 600;
    padding: 10px 25px;
    margin-top: 10px;
    transition: all 0.3s ease;
    
    i {
      margin-right: 8px;
    }
    
    &:hover {
      background-color: white;
      color: #333;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 991.98px) {
    .col-lg-4 {
      margin-top: 20px;
    }
  }
}

.testimonials-section {
  padding: 100px 0;
  background: white;
  position: relative;
  
  h2 {
    text-align: center;
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 60px;
    color: #333;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: -15px;
      width: 80px;
      height: 3px;
      background-color: var(--primary-color);
      transform: translateX(-50%);
    }
  }
  
  .carousel-inner {
    padding: 20px 0;
  }
  
  .testimonial-card {
    display: flex;
    padding: 40px;
    background: white;
    border-radius: 15px;
    box-shadow: 0 15px 50px rgba(0,0,0,0.08);
    max-width: 800px;
    margin: 0 auto;
    position: relative;
    
    @media (max-width: 767.98px) {
      flex-direction: column;
      text-align: center;
      padding: 30px 20px;
    }
    
    &::before {
      content: '"';
      position: absolute;
      top: 20px;
      left: 30px;
      font-size: 6rem;
      color: var(--primary-color);
      font-family: Georgia, serif;
      opacity: 0.1;
      line-height: 1;
      z-index: 0;
    }
    
    &::after {
      content: '"';
      position: absolute;
      bottom: 10px;
      right: 30px;
      font-size: 6rem;
      color: var(--primary-color);
      font-family: Georgia, serif;
      opacity: 0.1;
      line-height: 1;
      z-index: 0;
    }
    
    .testimonial-image {
      flex: 0 0 120px;
      margin-right: 30px;
      position: relative;
      z-index: 1;
      
      @media (max-width: 767.98px) {
        margin: 0 auto 20px;
      }
      
      img {
        width: 120px;
        height: 120px;
        border-radius: 50%;
        object-fit: cover;
        border: 5px solid rgba(0, 206, 201, 0.2);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      }
    }
    
    .testimonial-content {
      flex: 1;
      position: relative;
      z-index: 1;
    }
    
    .testimonial-text {
      position: relative;
      margin-bottom: 20px;
      
      .quote-icon {
        font-size: 2rem;
        color: var(--primary-color);
        opacity: 0.3;
        margin-bottom: 15px;
      }
      
      p {
        font-size: 1.1rem;
        font-style: italic;
        line-height: 1.7;
        color: #495057;
        margin-bottom: 0;
        text-align: left;
        
        @media (max-width: 767.98px) {
          text-align: center;
          font-size: 1rem;
        }
      }
    }
    
    .testimonial-author {
      text-align: left;
      
      @media (max-width: 767.98px) {
        text-align: center;
      }
      
      h4 {
        font-weight: 700;
        margin-bottom: 5px;
        color: #333;
        font-size: 1.2rem;
      }
      
      p {
        color: var(--primary-color);
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0;
      }
    }
  }
  
  .carousel-control-prev,
  .carousel-control-next {
    width: 40px;
    height: 40px;
    background-color: var(--primary-color);
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
    }
  }
  
  .carousel-control-prev {
    left: -20px;
  }
  
  .carousel-control-next {
    right: -20px;
  }
  
  .carousel-control-prev-icon,
  .carousel-control-next-icon {
    width: 20px;
    height: 20px;
  }
}

.cta-section {
  padding: 100px 0;
  background: linear-gradient(45deg, #00CEC9, #20e3de);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.1);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -80px;
    left: -80px;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.05);
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  .cta-content {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
    
    h2 {
      font-size: 3rem;
      font-weight: 800;
      margin-bottom: 1.5rem;
      text-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
      animation: pulse 2s infinite;
    }
    
    p {
      font-size: 1.4rem;
      margin-bottom: 2.5rem;
      opacity: 0.9;
      font-weight: 300;
      line-height: 1.7;
    }
    
    .btn-light {
      padding: 15px 40px;
      font-size: 1.1rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      border-radius: 50px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      border: 2px solid white;
      position: relative;
      overflow: hidden;
      z-index: 1;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 0%;
        height: 100%;
        background-color: var(--dark-color);
        transition: all 0.5s cubic-bezier(0.7, 0, 0.2, 1);
        z-index: -1;
      }
      
      &:hover {
        color: white;
        transform: translateY(-5px);
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
        
        &::before {
          width: 100%;
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}
</style>